// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { useEffect, useRef, useState } from 'react';
import { messages, useMessages } from '../i18n.js';
import type { SurveyDueDto } from '../api/client.js';

/**
 * The experience survey (founder, 2026-09-20). Two questions, both required, shown once a person
 * has been answered a handful of times — and until it is submitted they cannot send another
 * message.
 *
 * The two questions come from the server so that every client asks the same thing; everything
 * around them is interface wording and lives here. It is a modal dialog and deliberately has no
 * close button: what makes it mandatory is that there is no way past it but through it. Whether it
 * appears at all is the server's decision, and the server never asks it on a conversation the Guard
 * has closed — so this form can never stand between a person in a bad moment and their next
 * message.
 */
const T = messages({
  ar: {
    required: 'الإجابة عن السؤالين مطلوبة للمتابعة.',
    opinionPrompt: 'اكتب ما تراه بصراحة — ما نفعك وما لم ينفعك.',
    submit: 'إرسال والمتابعة',
    sending: 'جارٍ الإرسال…',
    missingOpinion: 'اكتب رأيك أولاً.',
    missingChoice: 'اختر «نعم» أو «لا».',
    failed: 'تعذّر الإرسال. أعد المحاولة.',
  },
  en: {
    required: 'Both answers are needed to carry on.',
    opinionPrompt: 'Say it plainly — what helped and what did not.',
    submit: 'Send and carry on',
    sending: 'Sending…',
    missingOpinion: 'Write your answer first.',
    missingChoice: 'Choose Yes or No.',
    failed: 'That did not send. Try again.',
  },
});

export function SurveyForm({
  survey,
  language,
  onSubmit,
}: {
  readonly survey: Extract<SurveyDueDto, { due: true }>;
  readonly language: 'ar' | 'en';
  readonly onSubmit: (input: {
    readonly opinion: string;
    readonly wouldPay: 'yes' | 'no';
  }) => Promise<boolean>;
}): React.JSX.Element {
  const t = useMessages(T);
  const [opinion, setOpinion] = useState('');
  const [wouldPay, setWouldPay] = useState<'yes' | 'no' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const box = useRef<HTMLTextAreaElement | null>(null);
  const dialog = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    box.current?.focus();
  }, []);

  // The keyboard stays inside while the form is up, which is what a modal dialog owes a person who
  // cannot see it. There is deliberately no Escape handler: Escape closes a dialog, and this one
  // does not close.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab' || dialog.current === null) return;
      const focusable = dialog.current.querySelectorAll<HTMLElement>(
        'button, textarea, [href], input, select, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const send = async (): Promise<void> => {
    if (opinion.trim().length === 0) {
      setError(t.missingOpinion);
      box.current?.focus();
      return;
    }
    if (wouldPay === null) {
      setError(t.missingChoice);
      return;
    }
    setError(null);
    setSending(true);
    const ok = await onSubmit({ opinion: opinion.trim(), wouldPay });
    setSending(false);
    if (!ok) setError(t.failed);
  };

  return (
    <div className="survey-scrim" role="presentation">
      <div
        className="survey"
        role="dialog"
        aria-modal="true"
        aria-labelledby="survey-title"
        ref={dialog}
      >
        <h2 id="survey-title">{survey.title[language]}</h2>
        <p className="survey-required">{t.required}</p>

        <label htmlFor="survey-opinion">{survey.opinion[language]}</label>
        <textarea
          id="survey-opinion"
          ref={box}
          rows={4}
          maxLength={survey.opinionMaxLength}
          value={opinion}
          placeholder={t.opinionPrompt}
          onChange={(event) => {
            setOpinion(event.target.value);
          }}
        />

        <p className="survey-question" id="survey-pay-label">
          {survey.wouldPay[language]}
        </p>
        {/* A pill set, because every pill rule in the stylesheet is scoped to one: outside it these
            two were the browser's own grey boxes and pressing one changed nothing a person could
            see (founder, 2026-09-21). */}
        <div
          className="pill-set binary survey-choice"
          role="group"
          aria-labelledby="survey-pay-label"
        >
          {(['yes', 'no'] as const).map((choice) => (
            <button
              key={choice}
              type="button"
              className="pill"
              aria-pressed={wouldPay === choice}
              onClick={() => {
                setWouldPay(choice);
                setError(null);
              }}
            >
              {choice === 'yes' ? survey.yesLabel[language] : survey.noLabel[language]}
            </button>
          ))}
        </div>

        {error !== null && (
          <p className="survey-error" role="alert">
            {error}
          </p>
        )}
        <button
          type="button"
          className="survey-send"
          disabled={sending}
          onClick={() => void send()}
        >
          {sending ? t.sending : t.submit}
        </button>
      </div>
    </div>
  );
}
